import React, { ChangeEvent } from "react";
import { DateTime } from "luxon";

import withTitle from "../withTitle";
import { V2 } from "../../Api";
import { validatePhoneNumber, formatTelephonePartial, validateEmailOptional } from "../../Utilities";
import { WellKnownGroups, IAuthProps } from "../AuthContext";
import Indicator from "../Indicator";
import withAuth from "../withAuth";
import withProps from "../withProps";
import FileUploadDialog from "../FileUploadDialog";
import ValidateInput from "../ValidateInput";
import { RouteComponentProps } from "react-router";

interface IOutreachChartInputViewState {
    isSubmitting: boolean | string;
    isUploadDialogOpen: boolean;

    showSuccess: boolean;
    insuranceProviders: any[];

    chartNumber: string;
    name: string;
    note: string;

    birthDate: string;
    sex: string;
    phone: string;
    email: string;
    insuranceProvider: string;

    uploadJob?: any;
}

interface IOutreachChartInputViewProps extends IAuthProps, RouteComponentProps<any> {
    type?: "upload" | "self";
}

class OutreachChartInputView extends React.Component<IOutreachChartInputViewProps, IOutreachChartInputViewState> {
    private _updateTimer?: number;
    private _refs: ValidateInput[];

    constructor(props: Readonly<IOutreachChartInputViewProps>) {
        super(props);

        const state: IOutreachChartInputViewState = {
            isSubmitting: false,
            isUploadDialogOpen: false,
            showSuccess: false,
            insuranceProviders: [],

            chartNumber: "",
            name: "",
            note: "",

            birthDate: "",
            sex: "F",
            phone: "",
            email: "",
            insuranceProvider: ""
        };

        this.state = state;

        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.submitData = this.submitData.bind(this);
        this.toggleUploadDialog = this.toggleUploadDialog.bind(this);
        this.handleUploadSuccess = this.handleUploadSuccess.bind(this);
        this.updateReportStatus = this.updateReportStatus.bind(this);
        this.submitPatientCare = this.submitPatientCare.bind(this);
        this.submitPatientCareSelf = this.submitPatientCareSelf.bind(this);
        this.submitSurgicalConsultant = this.submitSurgicalConsultant.bind(this);
        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.validateBirthdate = this.validateBirthdate.bind(this);

        this._refs = [];
    }

    public componentWillUnmount() {
        if (this._updateTimer)
            window.clearTimeout(this._updateTimer);
    }

    public async componentDidMount() {
        if (this.props.type === "self") {
            try {
                const insuranceProviders = await V2.get<any[]>("accounting/insurance-companies/summary");

                this.setState({
                    insuranceProviders
                });
            }
            catch (e) {
                // Couldn't load insurance companies
            }
        }
    }

    public render() {
        if (this.props.type === "upload") {
            return this.renderUpload();
        }

        if (this.props.type === "self") {
            return this.renderExtended();
        }

        return (
            <div className="viewer-root">
                <form className="input-form">
                    <div className="column-root">
                        <div className="row-root">
                            <section className="info-block">
                                <header>
                                    <h3>Patient Information</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="chart-number">Chart Number</label>
                                        <input type="text" id="chart-number" name="chartNumber" value={this.state.chartNumber} onChange={this.handleInputChanged} />
                                    </div>
                                    <div className="input-form-group">
                                        <label htmlFor="name">Name</label>
                                        <input type="text" id="name" name="name" value={this.state.name} onChange={this.handleInputChanged} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="note">Note</label>
                                        <textarea id="note" className="input-textarea-medium" name="note" value={this.state.note} onChange={this.handleInputChanged} />
                                    </div>
                                </section>
                            </section>
                            <section>
                                {this.props.authContext.hasRight("oreach:sc:li")
                                    ? <div className="action-panel">
                                        <button type="button" className="button" onClick={this.submitPatientCare}>Add for Patient Care Rep</button>
                                        <button type="button" className="button push-right" onClick={this.submitSurgicalConsultant}>Add for Surgical Consultant</button>
                                    </div>
                                    : <div className="action-panel">
                                        <button type="button" className="button" onClick={this.submitPatientCare}>Add for Patient Care Rep</button>
                                    </div>
                                }
                            </section>
                        </div>
                        <section className="spacer" />
                    </div>
                    &nbsp;
                    {this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <div className="error-text">{this.state.isSubmitting}</div>
                            : this.state.showSuccess
                                ? <div><Indicator color="green" icon="check" text="Saved!" /><br />&nbsp;</div>
                                : null}
                </form>
            </div>
        );
    }

    private renderUpload() {
        return (
            <div className="viewer-root">
                <FileUploadDialog isOpen={this.state.isUploadDialogOpen} accept=".csv,text/csv" endpoint={`survey/outreach/group/${WellKnownGroups.AppointmentSpecialist}/lead-upload`} descriptionOverride="-" closeOnSuccess={true} onUploadSuccess={this.handleUploadSuccess} showProgress={true} onRequestClose={this.toggleUploadDialog} />
                <div>Use the button below to upload the annual appointment report CSV file.</div>
                &nbsp;
                <div className="action-panel-dialog">
                    <button type="button" className="button text-with-icon" onClick={this.toggleUploadDialog}><i className="icon-upload" /><span>Upload File</span></button>
                </div>
                {!!this.state.uploadJob
                    ? <div>
                        <br />Status: {this.state.uploadJob?.status}
                        <br />{this.state.uploadJob?.recordsProcessed} record(s) processed.
                    </div>
                    : null
                }
            </div>
        );
    }

    private renderExtended() {
        return (
            <div className="viewer-root">
                <form className="input-form">
                    <div className="column-root">
                        <div className="row-root">
                            <section className="info-block">
                                <header>
                                    <h3>Patient Information</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-name" className="required">Name</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name" name="name" value={this.state.name} onChange={this.handleInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group col-50">
                                        <label htmlFor="frm-birthdate" className="required">Date of Birth</label>
                                        <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-birthdate" name="birthDate" value={this.state.birthDate} onChange={this.handleInputChanged} placeholder="mm/dd/yyyy" autoComplete="off_please_chrome" validator={this.validateBirthdate} invalidMessage="Please double-check date" />
                                    </div>
                                    <div className="input-form-group col-25">
                                        <label htmlFor="frm-sex">Sex</label>
                                        <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-sex" name="sex" value={this.state.sex} onChange={this.handleInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                            <option value="" />
                                            <option value="F">Female</option>
                                            <option value="M">Male</option>
                                        </ValidateInput>
                                    </div>
                                    <div className="input-form-group col-25">
                                        <label htmlFor="frm-chart" className="required">Chart #</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-chart" name="chartNumber" value={this.state.chartNumber} onChange={this.handleInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-phone" className="required">Phone Number</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-phone" name="phone" value={this.state.phone} onChange={this.handleInputChanged} placeholder="(xxx) xxx-xxxx" autoComplete="off_please_chrome" filter={formatTelephonePartial} isRequired={false} validator={validatePhoneNumber} />
                                    </div>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-email">Email Address</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-email" name="email" value={this.state.email} onChange={this.handleInputChanged} placeholder="Email Address" autoComplete="off_please_chrome" validator={validateEmailOptional} invalidMessage="Invalid Email address" isRequired={false} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-insurance" className="required">Insurance Provider</label>
                                        <ValidateInput type="select-other" ref={this.receiveValidationRef} id="frm-insurance" name="insuranceProvider" value={this.state.insuranceProvider} onChange={this.handleInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                            <option value="" />
                                            {this.state.insuranceProviders.map((a: any) => <option key={a.id} value={a.shortName || a.name}>{a.name}</option>)}
                                        </ValidateInput>
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="note" className="required">Note</label>
                                        <ValidateInput type="textarea" ref={this.receiveValidationRef} id="note" className="input-textarea-medium" name="note" value={this.state.note} onChange={this.handleInputChanged} isRequired={true} />
                                    </div>
                                </section>
                            </section>
                            <section>
                                {this.props.authContext.hasRight("oreach:sc:li")
                                    ? <div className="action-panel">
                                        <button type="button" className="button" onClick={this.submitPatientCareSelf}>Add & Outreach</button>
                                        <button type="button" className="button push-right" onClick={this.submitSurgicalConsultant}>Add for Surgical Consultant</button>
                                    </div>
                                    : <div className="action-panel">
                                        <button type="button" className="button" onClick={this.submitPatientCare}>Add for Patient Care Rep</button>
                                    </div>
                                }
                            </section>
                        </div>
                        <section className="spacer" />
                    </div>
                    &nbsp;
                    {this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <div className="error-text">{this.state.isSubmitting}</div>
                            : this.state.showSuccess
                                ? <div><Indicator color="green" icon="check" text="Saved!" /><br />&nbsp;</div>
                                : null}
                </form>
            </div>
        );
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLTextAreaElement>) {
        const name = e.currentTarget.name;
        const value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            [name]: value
        } as any);
    }

    private submitPatientCare() {
        this.submitData(WellKnownGroups.PatientCareRep);
    }

    private submitPatientCareSelf() {
        this.submitData(WellKnownGroups.PatientCareRep, true);
    }

    private submitSurgicalConsultant() {
        this.submitData(WellKnownGroups.SurgicalConsultant);
    }

    private async submitData(group: string, isSelf = false) {
        try {
            if (isSelf) {
                let allGood = true;
                for (const item of this._refs) {
                    allGood = item.validate() && allGood;
                }

                if (!allGood) {
                    this.setState({
                        isSubmitting: "Please fill in all required fields"
                    });
                    return;
                }
            }
            else {
                if (!this.state.chartNumber?.trim() || !this.state.name?.trim() || !this.state.note?.trim()) {
                    this.setState({
                        isSubmitting: "Please fill in all fields"
                    });
                    return;
                }
            }

            this.setState({
                isSubmitting: true
            });

            const data = {
                chartNumber: this.state.chartNumber,
                name: this.state.name,
                note: this.state.note,
                onlyCreator: isSelf,

                birthdate: this.state.birthDate,
                sex: this.state.sex,
                phone: this.state.phone,
                email: this.state.email,
                insuranceProvider: this.state.insuranceProvider
            };

            const result = await V2.post<any>(`survey/outreach/group/${group}/leads`, data);

            if (isSelf && result.id) {
                // Go to newly-created lead
                this.props.history.push(`/${this.groupToUrl(group)}/outreach/lead/${result.id}`, { inputOnEmpty: true });
            }

            this.setState({
                isSubmitting: false,

                chartNumber: "",
                name: "",
                note: "",

                birthDate: "",
                sex: "F",
                phone: "",
                email: "",
                insuranceProvider: "",

                showSuccess: true
            }, () => window.setTimeout(() => this.setState({ showSuccess: false }), 5000));
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e.error || "Could not submit data"
            });
        }
    }

    private toggleUploadDialog() {
        const { isUploadDialogOpen } = this.state;
        this.setState({
            isUploadDialogOpen: !isUploadDialogOpen
        });
    }

    private handleUploadSuccess(ids: string[], serverResponses?: any[]) {
        this.setState({
            uploadJob: serverResponses?.[0]?.job
        }, () => {
            this._updateTimer = window.setTimeout(this.updateReportStatus, 1000);
        });
    }

    private updateReportStatus() {
        V2.get<any>(`survey/outreach/group/${WellKnownGroups.AppointmentSpecialist}/lead-upload/status`)
            .then(job => {
                this.setState({
                    uploadJob: job
                });

                if (job.isRunning) {
                    this._updateTimer = window.setTimeout(this.updateReportStatus, 1000);
                }
            })
            .catch(e => {
                const uploadJob = this.state.uploadJob || {
                    id: "error",
                    isRunning: false,
                    recordsProcessed: 0
                };

                uploadJob.status = e.message || "Failed to process file";

                this.setState({
                    uploadJob
                });
            });

    }

    private receiveValidationRef(ref: ValidateInput) {
        this._refs.push(ref);
    }

    private validateBirthdate(value: string) {
        try {
            const local = DateTime.local();
            const date = DateTime.fromISO(value);

            return date.year > local.year - 100 && date.year < local.year;
        }
        catch (e) {
            return false;
        }
    }

    private groupToUrl(group: string) {
        switch (group) {
            case WellKnownGroups.SurgicalConsultant: return "surgicalconsult";
            case WellKnownGroups.PatientCareRep: return "telemarketing";
            case WellKnownGroups.AppointmentSpecialist: return "appointmentspecialist";
            default: throw new Error("Unknown group " + group);
        }
    }
}

export default withTitle("Lead Input", withAuth(OutreachChartInputView));
export const OutreachChartSelfInputView = withTitle("Lead Input", withProps(withAuth(OutreachChartInputView), { type: "self" }));
export const OutreachChartUploadView = withTitle("Annual Report Upload", withProps(OutreachChartInputView, { type: "upload" }));
